import { useCallback, useRef } from 'react';
import { usePrevious } from '@ecdlink/core';

export interface VisitStepQuestion {
  question: string;
  answer: unknown;
}

export interface VisitStepSection<
  T extends VisitStepQuestion = VisitStepQuestion
> {
  visitSection: string;
  questions: T[];
}

/**
 * Merge incoming visit-section answers into previously saved answers.
 * Matching questions are updated in place so going back and changing an
 * answer does not duplicate or drop other questions in the same section.
 */
export const mergeVisitSectionQuestions = <T extends VisitStepQuestion>(
  prevSections: VisitStepSection<T>[] | undefined,
  incoming: VisitStepSection<T>[]
): VisitStepSection<T>[] => {
  return incoming.flatMap((newSection) => {
    const oldSection = prevSections?.find(
      (section) => section.visitSection === newSection.visitSection
    );
    const questionsFromOldSection = oldSection?.questions || [];
    const otherSections =
      prevSections?.filter(
        (section) => section.visitSection !== newSection.visitSection
      ) || [];

    const questionMap = new Map(
      questionsFromOldSection.map((question) => [question.question, question])
    );
    newSection.questions.forEach((question) => {
      questionMap.set(question.question, question);
    });

    return [
      ...otherSections,
      {
        visitSection: newSection.visitSection,
        questions: Array.from(questionMap.values()),
      },
    ];
  });
};

export const getVisitStepKey = (
  step: { displayName?: string; name?: string },
  index: number
) => `${step.displayName || step.name || 'visit-step'}-${index}`;

/**
 * Tracks which visit-form steps have already been shown so they can stay
 * mounted (and keep local answers) when the user hits back.
 */
export const useVisitedVisitSteps = (
  currentStep?: number,
  steps?: { displayName?: string; name?: string }[]
) => {
  const stepIndex = Number(currentStep) || 0;
  const highestVisitedRef = useRef(0);
  const previousStep = usePrevious(stepIndex);
  const stepsSignature = (steps ?? [])
    .map((step) => step.displayName || step.name || '')
    .join('|');
  const previousSignature = usePrevious(stepsSignature);

  if (previousSignature !== undefined && previousSignature !== stepsSignature) {
    highestVisitedRef.current = stepIndex;
  }

  const isRevisit =
    previousStep !== undefined && stepIndex <= highestVisitedRef.current;

  if (stepIndex > highestVisitedRef.current) {
    highestVisitedRef.current = stepIndex;
  }

  const hasVisitedStep = useCallback((index: number) => {
    return index <= highestVisitedRef.current;
  }, []);

  return {
    stepIndex,
    isRevisit,
    maxMountedIndex: highestVisitedRef.current,
    hasVisitedStep,
  };
};
